import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { Home, TrendingUp, Receipt, ArrowRight, Loader2 } from "lucide-react";

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString("en-AU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

/**
 * PersonalTaxQuickView — compact 3-card summary of the current FY's personal
 * tax rollup (net rental, investments grossed-up, personal deductions) with a
 * link to the full /personal-tax editor. Mounted at the top of /tax so the
 * tradie sees personal numbers alongside their BAS/TPAR.
 */
export default function PersonalTaxQuickView() {
  const [summary, setSummary] = useState(null);
  const [fallback, setFallback] = useState(false); // true when we auto-fell-back to a previous FY
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const currentFy = (() => {
        const now = new Date();
        return now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
      })();
      try {
        // 1) Try current FY first
        const { data } = await api.get("/personal-tax/summary");
        if (data.entry_count > 0) {
          setSummary(data);
          setFallback(false);
          return;
        }
        // 2) Auto-fall-back to most recent FY with data (walk back up to 3 FYs).
        //    Keeps end-of-year users from seeing an all-zero card in July.
        for (let step = 1; step <= 3; step++) {
          const fy = currentFy - step;
          try {
            const { data: prev } = await api.get(`/personal-tax/summary?fy=${fy}`);
            if (prev.entry_count > 0) {
              setSummary(prev);
              setFallback(true);
              return;
            }
          } catch (_e) { /* try next FY */ }
        }
        // 3) Still empty — show current FY (with zeroes) as final state.
        setSummary(data);
        setFallback(false);
      } catch (err) {
        devLog.warn("personal-tax summary load failed", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section className="bi-surface p-4 space-y-3" data-testid="personal-tax-quickview">
      <div className="flex items-baseline justify-between">
        <div className="font-heading font-bold inline-flex items-center gap-2">
          <Receipt className="w-4 h-4 text-[#FF9500]" />
          Personal Tax
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading" data-testid="qv-fy-label">
          {summary?.fy_label || "Current FY"}
          {fallback && (
            <span className="ml-1 text-[#FF9500] normal-case tracking-normal" data-testid="qv-fallback-tag">
              · showing last FY with data
            </span>
          )}
        </div>
      </div>

      {loading && (
        <div className="text-white/40 text-xs inline-flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      )}

      {!loading && summary && (
        <div className="grid grid-cols-3 gap-2">
          <MiniCard
            testid="qv-rental"
            icon={<Home className="w-3.5 h-3.5 text-[#25D366]" />}
            label="Rental net"
            value={fmt(summary.rental.net)}
            hint={`${summary.rental.property_count || 0} propert${summary.rental.property_count === 1 ? "y" : "ies"}`}
          />
          <MiniCard
            testid="qv-investments"
            icon={<TrendingUp className="w-3.5 h-3.5 text-[#60A5FA]" />}
            label="Investments"
            value={fmt(summary.investments.grossed_up)}
            hint={`FC ${fmt(summary.investments.franking_credits)}`}
          />
          <MiniCard
            testid="qv-deductions"
            icon={<Receipt className="w-3.5 h-3.5 text-[#FF9500]" />}
            label="Deductions"
            value={fmt(summary.deductions.total)}
            hint={`${Object.keys(summary.deductions.by_category || {}).length} cats`}
          />
        </div>
      )}

      <Link
        to="/personal-tax"
        data-testid="qv-open-editor"
        className="w-full h-10 rounded-lg bi-ghost-btn text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5 hover:brightness-125 active:scale-95 transition"
      >
        Manage in Personal Tax <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </section>
  );
}

function MiniCard({ testid, icon, label, value, hint }) {
  return (
    <div className="rounded-lg bg-white/[0.03] border border-white/5 p-2.5" data-testid={testid}>
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] text-white/40 font-heading">
        {icon}<span className="truncate">{label}</span>
      </div>
      <div className="font-heading font-extrabold text-white text-sm truncate mt-0.5">{value}</div>
      {hint && <div className="text-[9px] text-white/40 truncate">{hint}</div>}
    </div>
  );
}
